import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { selectLayoutUser, selectCurrentProject, selectInstanceInfo, selectMenuBadges } from '@/entities/layout';
import {
  selectNavigationItemsWithState,
  selectIsCollapsed,
  toggleItem,
  toggleSidebar,
  type NavigationItemWithState
} from '@/entities/navigation';

import { NavigationItem, SidebarHeader, SidebarFooter } from '@/features/sidebar-navigation';
import { SidebarToggle } from '@/shared/ui/sidebar-toggle';
import { cn } from '@/lib/utils';
import type { AppDispatch, RootState } from '@/app/types/store';

export const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const user = useSelector(selectLayoutUser);
  const currentProject = useSelector(selectCurrentProject);
  const instanceInfo = useSelector(selectInstanceInfo);
  const badges = useSelector(selectMenuBadges);
  const isCollapsed = useSelector(selectIsCollapsed);
  const navigationItems = useSelector((state: RootState) => selectNavigationItemsWithState(state, location.pathname));

  const handleToggleItem = (code: string) => {
    dispatch(toggleItem(code));
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const itemsWithBadges = useMemo(
    () =>
      navigationItems.map((item) => ({
        ...item,
        badge: badges?.[item.code as keyof typeof badges] || item.badge
      })) as NavigationItemWithState[],
    [navigationItems, badges]
  );

  return (
    <aside
      className={cn(
        'relative flex flex-col h-screen bg-background border-r transition-all duration-300 shrink-0',
        isCollapsed ? 'w-16' : 'w-64'
      )}>
      <SidebarHeader
        projectName={currentProject?.name || ''}
        instanceName={instanceInfo?.name || ''}
        isCluster={instanceInfo?.isCluster || false}
        isCollapsed={isCollapsed}
      />

      <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
        <div className="mb-6 px-3">
          {!isCollapsed && (
            <h3 className="text-xs font-medium text-muted-foreground mb-2 px-2 flex items-center justify-between group cursor-pointer">
              {t('sidebar.menu')}
              <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
          )}
          <ul className="space-y-1">
            {itemsWithBadges.map((item) => (
              <li key={item.code}>
                <NavigationItem item={item} isCollapsed={isCollapsed} onToggle={handleToggleItem} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {user && <SidebarFooter user={user} isCollapsed={isCollapsed} />}
      <SidebarToggle isCollapsed={isCollapsed} onToggle={handleToggleSidebar} />
    </aside>
  );
};

export default Sidebar;
